import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { apiClient, extractApiError } from '../api/axios';
import { API } from '../constants/api';
import { DEFAULT_PAGE_SIZE } from '../constants/ui';

const initialState = {
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  total: null,
  loading: false,
  error: '',
  success: false,
  deleted: false,
  products: [],
  productModal: false,
  categories: [],
};

export const fetchProducts = createAsyncThunk('products/fetchProducts', async (filter, { rejectWithValue }) => {
  try {
    const response = await apiClient.get(API.products.list, { params: filter });
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

export const createProduct = createAsyncThunk('products/createProduct', async (data, { rejectWithValue }) => {
  try {
    const response = await apiClient.post(API.products.list, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

export const updateProduct = createAsyncThunk(
  'products/updateProduct',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(API.products.byId(id), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const deleteProduct = createAsyncThunk('products/deleteProduct', async ({ id }, { rejectWithValue }) => {
  try {
    const response = await apiClient.del(API.products.byId(id));
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

export const fetchCategories = createAsyncThunk('products/fetchCategories', async (_, { rejectWithValue }) => {
  try {
    const response = await apiClient.get(API.products.categories);
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

export const productsSlice = createSlice({
  name: 'products',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchProducts.pending, (state) => {
      state.loading = true;
      state.error = '';
      state.success = false;
    });
    builder.addCase(fetchProducts.fulfilled, (state, action) => {
      state.loading = false;
      state.page = action.payload.page;
      state.pageSize = action.payload.page_size;
      state.total = action.payload.total;
      state.products = action.payload.results;
      state.success = true;
    });
    builder.addCase(fetchProducts.rejected, (state, action) => {
      state.error = action.payload;
      toast.error(action.payload);
      state.loading = false;
      state.success = false;
    });

    builder.addCase(fetchCategories.pending, (state) => {
      state.loading = true;
      state.error = '';
      state.success = false;
    });
    builder.addCase(fetchCategories.fulfilled, (state, action) => {
      state.loading = false;
      state.categories = action.payload.results;
      state.success = true;
    });
    builder.addCase(fetchCategories.rejected, (state, action) => {
      state.error = action.payload;
      toast.error(action.payload);
      state.loading = false;
      state.success = false;
    });

    builder.addCase(createProduct.pending, (state) => {
      state.loading = true;
      state.error = '';
      state.success = false;
    });
    builder.addCase(createProduct.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      toast.success('Mahsulot yaratildi');
      // The thunk returns the product object directly (like updateProduct);
      // .data was undefined and unshifted a blank row that crashed the list.
      if (action.payload) {
        state.products.unshift(action.payload);
      }
    });
    builder.addCase(createProduct.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      toast.error(action.payload);
      state.success = false;
    });

    builder.addCase(updateProduct.pending, (state) => {
      state.loading = true;
      state.error = '';
      state.success = false;
    });
    builder.addCase(updateProduct.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      toast.success('Mahsulot yangilandi');
      const updatedProduct = action.payload;
      const index = state.products.findIndex((product) => product.id === updatedProduct.id);
      if (index !== -1) {
        state.products[index] = updatedProduct;
      }
    });
    builder.addCase(updateProduct.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      toast.error(action.payload);
      state.success = false;
    });

    builder.addCase(deleteProduct.fulfilled, (state) => {
      state.deleted = true;
    });
    builder.addCase(deleteProduct.rejected, (state, action) => {
      toast.error(action.payload);
    });
  },
  reducers: {
    setProductModal: (state, { payload }) => {
      state.productModal = payload;
    },
    setPage: (state, { payload }) => {
      state.page = payload;
    },
    resetDeleted: (state) => {
      state.deleted = false;
    },
  },
});

export const { setProductModal, setPage, resetDeleted } = productsSlice.actions;

export default productsSlice.reducer;
